/**
 * A contact's photo, with a gender-aware placeholder and an initials fallback.
 *
 * Two sizes, both from the design system's density tokens: `thumb` for the
 * dense table, `avatar` for the profile header. Nothing in between — a third
 * size would be a per-screen guess.
 *
 * The photo is an attachment like any other (owner_type CONTACT_PHOTO); the
 * upload goes through the same initiate/PUT/complete flow as a file on an
 * interaction, with an images-only allowlist and a smaller cap enforced
 * server-side.
 *
 * THE THREE STATES, in order:
 *
 *   1. a real photo    — always wins;
 *   2. no photo, gender on file — the ♂/♀ glyph;
 *   3. no photo, no gender      — the initials, which is what this component
 *      has always shown and the fallback the deployment already has.
 *
 * **Why a glyph and not an illustration.** No image assets ship with this
 * client — only the Vazirmatn faces — and adding a pair of drawn avatars would
 * be a new asset for a circle the size of a table thumbnail. `lucide-react` is
 * already the icon set
 * everywhere, and `Mars`/`Venus` are the only pair in it that actually
 * distinguishes the two; `User` and `UserRound` differ by shoulder shape, not
 * by gender. **No colour coding**: both glyphs render in the same muted token
 * as the initials they replace, because pink-and-blue would be a second, worse
 * way of saying what the glyph already says.
 *
 * The trade-off is real and deliberate: a named male contact shows ♂ rather
 * than their own initials, which carry more information. That is what the
 * placeholder was asked to do, and gender-unknown still keeps the initials.
 */
import { Spinner } from '@meshhub/design-tokens/ui';
import { Camera, Mars, Venus } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { uploadFile } from '../lib/files.js';
import { S } from '../lib/strings.js';

/** Initials from a display name — the fallback when there is no photo. */
function initials(name) {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '؟';
  if (parts.length === 1) return parts[0].slice(0, 1);
  return `${parts[0].slice(0, 1)}${parts[parts.length - 1].slice(0, 1)}`;
}

/** MALE and FEMALE only. OTHER and unset both fall through to the initials —
 *  a glyph for "other" would be guessing at how somebody wants to be shown. */
const GENDER_GLYPH = { MALE: Mars, FEMALE: Venus };

export function ContactPhoto({ name, url, gender, size = 'thumb' }) {
  const box =
    size === 'avatar'
      ? 'h-avatar w-avatar text-lg'
      : 'h-thumb w-thumb text-xs';

  if (url) {
    return (
      <img
        src={url}
        alt=""
        className={`${box} shrink-0 rounded-full border border-border object-cover`}
      />
    );
  }

  const Glyph = GENDER_GLYPH[gender];
  const label = Glyph ? S.contacts.detail.genderAvatar[gender] : undefined;

  return (
    <span
      // The glyph SAYS something (this contact is a man / a woman), so unlike
      // the initials it is not decorative and carries a label. The initials
      // stay aria-hidden: they repeat the name in the cell beside them.
      aria-hidden={Glyph ? undefined : 'true'}
      role={Glyph ? 'img' : undefined}
      aria-label={label}
      title={label}
      className={`${box} flex shrink-0 items-center justify-center rounded-full border
                  border-border bg-surface-hover font-medium text-text-muted`}
    >
      {Glyph ? <Glyph size={size === 'avatar' ? 22 : 14} aria-hidden="true" /> : initials(name)}
    </span>
  );
}

/**
 * The profile-header photo, clickable to upload or replace.
 *
 * `accept="image/*"` narrows the OS file picker; it is a convenience, not the
 * control — the server's allowlist is what actually decides.
 */
export function ContactPhotoUpload({ contactId, name, url, gender, onUploaded, disabled }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function handleFile(file) {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadFile({ file, ownerType: 'CONTACT_PHOTO', ownerId: contactId });
      await onUploaded?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      // Reset so choosing the same file twice fires a change event again.
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="flex flex-col items-center gap-1">
      <button
        type="button"
        disabled={disabled || busy}
        onClick={() => inputRef.current?.click()}
        title={url ? S.contacts.detail.replacePhoto : S.contacts.detail.uploadPhoto}
        aria-label={url ? S.contacts.detail.replacePhoto : S.contacts.detail.uploadPhoto}
        className="group relative rounded-full focus-visible:outline focus-visible:outline-2
                   focus-visible:outline-offset-1 focus-visible:outline-primary
                   disabled:opacity-60"
      >
        <ContactPhoto name={name} url={url} gender={gender} size="avatar" />
        <span
          className="absolute inset-0 flex items-center justify-center rounded-full bg-text/50
                     text-text-inverse opacity-0 transition-opacity group-hover:opacity-100"
        >
          {busy ? <Spinner /> : <Camera size={18} aria-hidden="true" />}
        </span>
      </button>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => handleFile(event.target.files?.[0])}
      />

      {error && (
        <span role="alert" className="max-w-avatar text-center text-xs text-danger">
          {error}
        </span>
      )}
    </div>
  );
}
